import { useForceUpdateOnResize } from "../../commons/Utils";
import React, { LegacyRef, useEffect, useRef, useState } from "react";
import { DialogContainer } from "oc-ui/dialogs/Dialogs";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { I18N } from "oc-I18N";
import * as _ from "lodash";
import { Raster } from "./Raster";
import { CoordinateSystem, coordinateSystem } from "./CoordinateSystem";
import { PageButton } from "../PageButtons";
import { GrundrissState, teilStyle } from "../../arrangement/grundrissFunctions";
import { ChatBot } from "../chatbot/ChatBot";
import { LeftMenu } from "./LeftMenu";
import { RightMenu } from "./RightMenu";
import { TorDragging, TorState } from "./TorDragging";
import { Zaunlinien } from "./Zaunlinien";
import { MoveableTeil } from "./MoveableTeil";
import { Scalebar } from "./Scalebar";
import { useKeyDownHandler } from "./UseKeyDownHandler";
import { useGrundrissEvents } from "./UseGrundrissEvents";
import { sleep } from "oc-commons/Commons";
import { Price } from "../zauneditor/Price";
import { FertigButton } from "../zauneditor/FertigButton";

export const triggerChange = _.debounce(() => APP.trigger("changedConfiguration"), 600);

export function GrundrissPage({
  state,
  setNewState,
}: {
  state: Partial<GrundrissState>;
  setNewState: (newState: Partial<GrundrissState>) => void;
}) {
  const [updateId, watchUpdateRef] = useForceUpdateOnResize();
  const [editedKanteId, setEditedKanteId] = useState<number>(null);
  const keyboardSensor = useSensor(KeyboardSensor);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);
  const stage2dRef = useRef<HTMLDivElement>(null);
  const { setNodeRef } = useDroppable({
    id: "droppable",
  });
  const selectionRectangleRef = useRef<HTMLDivElement>(null);

  const cs: CoordinateSystem = state.pageFadedIn ? coordinateSystem() : null;

  const selected = APP.arrangement.selectedObjects[0] ?? null;
  const setSelected = async (s) => {
    await APP.arrangement.setSelectedObjects(s, false);
    await sleep(30); // Keine gute Lösung. Das ursprüngliche Problem ist, dass die AuswahlAccessoren Zeit brauchen zum umschalten, darauf muss gewartet werden.
    APP.updateUI();
  };
  const [aufbauCopyBuffer, setAufbauCopyBuffer] = useState(null);
  const [updateCounter, _setUpdateCounter] = useState(null);
  const forceUpdate = () => _setUpdateCounter({});
  const setDollyDirty = () => {
    forceUpdate();
    setSelected(null);
  };
  const [aufbautenChange, setAufbautenChange] = useState({});
  const dirty = () => {
    setAufbautenChange({});
    triggerChange();
    APP.dirty();
  };
  const [insideOperation, setInsideOperation] = useState(null);
  const [menuTeilDragging, setMenuTeilDragging] = useState(null);

  const [pointerDown, setPointerDown] = useState(false);

  useEffect(() => {
    const cl = document.querySelector(".cfg-page-Grundriss")?.classList;
    if (!cl) return;
    [
      "mode-zeichnen",
      "mode-radieren",
      "mode-verschieben",
      "mode-selektieren",
      "pointerdown",
      "mode-zoom",
    ].forEach((k) => cl.remove(k));
    const mode = "mode-" + state.modus;
    cl.add(mode);
    if (pointerDown) {
      cl.add("pointerdown");
    }
  }, [state.modus, pointerDown]);

  useEffect(() => {
    setSelected(null);
  }, [updateId]);

  useEffect(() => {
    if (state.pageFadedIn) {
      const agt = APP.arrangement;
      if (!state.ready) {
        setNewState({
          ready: true,
          kanten: agt.grundriss.kanten,
          points: agt.grundriss.kanten.flatMap((k) => k.ecken.flatMap((e) => e.punkt)),
        });
      }
    }
  }, [state.pageFadedIn, updateCounter, aufbautenChange, updateId]);

  useKeyDownHandler(selected, aufbauCopyBuffer, setAufbauCopyBuffer, setSelected, dirty);

  const torStateRef = useRef<TorState>();

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    handleClick,
    handleContextMenu,
  } = useGrundrissEvents({
    cs,
    state,
    setNewState,
    setPointerDown,
    setSelected,
    forceUpdate,
    setDollyDirty,
    selectionRectangleRef,
    torStateRef,
  });

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setMenuTeilDragging(event.active.data.current)}
      onDragEnd={(event) => {
        setMenuTeilDragging(null);
        dirty();
      }}
    >
      <div ref={watchUpdateRef as LegacyRef<HTMLDivElement>} className="grid-fullscreen" />

      <div className="grundriss"></div>

      <DialogContainer />

      {state.ready && <Raster cs={cs} />}

      {state.ready && <Scalebar cs={cs} />}

      <div
        ref={(ref) => {
          stage2dRef.current = ref;
          setNodeRef(ref);
        }}
        onContextMenu={handleContextMenu}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handlePointerMove}
        onWheel={handleWheel}
        className="stage2d grid-fullscreen page-initial-hidden"
        role="application"
        aria-label={I18N("aria::Grundriss-Zeichenfläche")}
        aria-roledescription={I18N("aria::Interaktive Zeichenfläche für den Grundriss")}
        style={{
          touchAction: "none",
          position: "relative",
        }} // Prevents default scrolling and zooming
      >
        {state.ready &&
          APP.arrangement.aufbauten.map((aufbau, index) => (
            <MoveableTeil
              key={aufbau.Key}
              cs={cs}
              aufbau={aufbau}
              insideOperation={insideOperation}
              modus={state.modus}
              editedKanteId={editedKanteId}
              setInsideOperation={setInsideOperation}
              selected={selected}
              setSelected={setSelected}
              setAufbautenChange={_.debounce(() => setAufbautenChange({}), 10)}
            />
          ))}

        {cs && state.ready && (
          <Zaunlinien cs={cs} editedKanteId={editedKanteId} setEditedKanteId={setEditedKanteId} />
        )}
      </div>

      <LeftMenu
        {...{
          stage2dRef,
          setNodeRef,
          handleContextMenu,
          handleClick,
          handlePointerDown,
          handlePointerUp,
          handlePointerMove,
          handleWheel,
          state,
          cs,
          insideOperation,
          setInsideOperation,
          selected,
          setSelected,
          setAufbautenChange,
          setNewState,
          setDollyDirty,
        }}
      />

      <RightMenu selected={selected} />

      <DragOverlay dropAnimation={null}>
        {menuTeilDragging?.Auswahlbild && (
          <div className="torMenuTeil">
            <div
              className="image"
              style={{ backgroundImage: `url(${menuTeilDragging.Auswahlbild})` }}
            />
            <div className="label">{menuTeilDragging.Name}</div>
          </div>
        )}
        {menuTeilDragging?.css && (
          <div
            className="aufbau"
            style={{
              ...teilStyle(menuTeilDragging),
            }}
          >
            {menuTeilDragging.Label}
          </div>
        )}
      </DragOverlay>

      <TorDragging cs={cs} torStateRef={torStateRef} forceUpdate={forceUpdate} />
      <div ref={selectionRectangleRef} className="selection-rectangle" />

      <PageButton
        className="back-button"
        buttonClassName="primary-button"
        label={I18N("Zurück")}
        pageName={APP.pageSequence[APP.pageIndex - 1]}
      />

      <div
        data-group-key="buttons-bottom-right"
        className={`bestell-buttons no-pointer-events fade page-initial-hidden ${APP.settings.tina.active && "chat-margin"
          }`}
        role="region"
        aria-label={I18N("aria::Preis und Abschluss")}
      >
        <Price />
        <FertigButton />
      </div>

      <ChatBot
        initialChatMessages={[I18N("TinaBegrüssungstext")]}
        avatarMessage={I18N("TinaGrundriss")}
        faqRootKey={"root_Grundriss"}
      />
    </DndContext>
  );
}
